import {
  Dialog, DialogBody,
  DialogHeader,
  DialogFooter,
  Typography,
} from "@material-tailwind/react";
import { useEffect } from "react";
import Select from "react-select";
import { Input } from "@material-tailwind/react";
import { getPriceVrientVendor, getVendorPrice } from "@/store/slices/rfqSlice";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import FilePreview from "@/components/FilePreview";
import { fetchCountry } from "@/store/slices/internalUsersSlice";
import { fetchCity } from "@/store/slices/productSlice";
import { useTranslation } from "react-i18next";
import useHasPermission from "@/components/useHasPermission";
import { useRef } from "react";

export function ViewVendorVarientPriceModal({ onClose, item }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('rfq');
  const { t: ct } = useTranslation("common");
  const { loading, vendors, rfq_product_vendors_prices } = useSelector(
    (state) => state.rfq
  );

  const dialogBodyRef = useRef(null);

  const hasViewProductPurchasePricePermission = useHasPermission(`product-purchase-price.view`);
  const hasViewProductSalePricePermission = useHasPermission(`product-sale-price.view`);

  const { countries } = useSelector((s) => s.internalUser);
  const { cities } = useSelector((s) => s.product);

  const initialValue = {
    rfq_product_id: item?.id,
    product_id: item?.product_id,
    vendors: [
      {
        vendor_id: null,
        quantity: 1,
        price: null,
        vendor_file: null,
        pickup_location: "",
        pickup_landmark: "",
        latitude: null,
        longitude: null,
      },
    ],
  };

  const { control, setValue, watch, reset } = useForm({
    defaultValues: initialValue,
  });

  const vendorRows = watch("vendors");

  useEffect(() => {
    dispatch(
      getPriceVrientVendor({
        color_id: item?.attribute_value_color_id,
        product_id: item?.product_id,
        size_id: item?.attribute_value_size_id,
        index: 0
      })
    );
    dispatch(
      getVendorPrice({
        rfq_product_id: item?.id,
      })
    );
    dispatch(fetchCountry());
  }, []);

  useEffect(() => {
    if (rfq_product_vendors_prices?.length > 0) {
      const formatted = rfq_product_vendors_prices.map((row) => ({
        vendor_id: row.vendor_id,
        quantity: row.quantity,
        price: row.price,
        previous_price: row.previous_price,
        vendor_file: row.vendor_file,
        vendor_type: row.vendor_type,
        country_id: row.country_id,
        city_id: row.city_id,
        pickup_location: row.pickup_location ?? "",
        pickup_landmark: row?.pickup_landmark ?? "",
        latitude: row.latitude ?? row.lat ?? null,
        longitude: row.longitude ?? row.lng ?? null,
      }));
      setValue("vendors", formatted);
      rfq_product_vendors_prices?.map((row, index) => {
        dispatch(fetchCity({ country_id: row.country_id, index: index }));
        dispatch(
          getPriceVrientVendor({
            color_id: item.attribute_value_color_id,
            product_id: item.product_id,
            size_id: item.attribute_value_size_id,
            country_id: row.country_id,
            city_id: row.city_id,
            index: index
          })
        );
      });
    } else {
      reset(initialValue);
    }
  }, [rfq_product_vendors_prices]);

  const close = () => {
    reset(initialValue);
    onClose();
  };

  const selectedColorVariant = item?.product?.product_varients?.find(
    (variant) => variant.color_id === item.attribute_value_color_id
  );

  const selectedSizeVariant = selectedColorVariant?.sizes?.find(
    (size) => size.size_id === item.attribute_value_size_id
  );

  const colorName = selectedColorVariant?.color;
  const colorHex = selectedColorVariant?.color_hex;
  const sizeName = selectedSizeVariant?.size;

  return (
    <Dialog open={true} handler={close} size="lg" className="rounded-2xl p-0">
      {loading && <ShowLoader />}
      <ScrollToTop targetRef={dialogBodyRef} />

      <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
        <div>
          <Typography className="text-2xl font-bold text-gray-900">
            {t('view_added_vendor_title')}
          </Typography>
        </div>
      </DialogHeader>

      <DialogBody ref={dialogBodyRef} className="px-6 max-h-[65vh] overflow-y-auto">
        <div className="flex items-center space-x-4 mb-6">
          <img
            src={item?.product_image || "/img/photo-gallery.png"}
            className="w-40 h-40 object-cover rounded-md border"
            alt="product"
          />
          <div>
            <p className="font-semibold text-gray-900 text-lg leading-tight">
              {t('product_name_label')} : {item?.product?.name}
            </p>
            <p className="text-sm font-medium text-gray-600">
              {t('total_quantity_label')}:{" "}
              <span className="text-gray-900">{item?.quantity}</span>
            </p>

            <div className="flex flex-wrap gap-2 mt-2">
              {/* Color & Size Badges */}
              {colorName && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded flex items-center gap-2 border">
                {ct('color')}: {colorName}
                {colorHex && (
                  <span
                    className="w-3 h-3 rounded-full border border-gray-300"
                    style={{ backgroundColor: colorHex }}
                  ></span>
                )}
              </span>}

              {sizeName && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded border">
                {ct('size')}: {sizeName}
              </span>}
            </div>
            {(hasViewProductPurchasePricePermission || hasViewProductSalePricePermission) && (
              <div className="flex gap-4 mt-2 border-t pt-2 border-dashed border-gray-300">
                {hasViewProductPurchasePricePermission && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                      {t('purchase_price_label')}
                    </p>
                    <p className="text-sm font-semibold text-blue-700">
                      {item?.product?.rfq_currency_code} {item?.unit_price}
                    </p>
                  </div>
                )}
                {hasViewProductSalePricePermission && (
                  <>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                        {t('sale_price_label')}
                      </p>
                      <p className="text-sm font-semibold text-green-700">
                        {item?.product?.rfq_currency_code} {item?.sale_price}
                      </p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                        {t('total_budget_label')}
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {item?.product?.rfq_currency_code}  {(Number(item?.sale_price || 0) * Number(item?.quantity || 0)).toFixed(2)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6 mt-4">
          {vendorRows.map((row, index) => {
            const fileToPreview = row?.vendor_file || null;
            return (
              <div
                key={index}
                className="border p-5 rounded-xl bg-white shadow-sm relative"
              >
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-800">
                    {t('vendor_number_label', { index: index + 1 })}
                  </h3>
                </div>

                {/* Country */}
                <div className="mb-4">
                  <Typography variant="small" className="font-medium capitalize">
                    {t('country_label')}
                  </Typography>
                  <Select
                    placeholder={t('select_country_placeholder')}
                    onChange={(country) => {
                      setValue(
                        `vendors.${index}.country_id`,
                        country?.value || null,
                        { shouldValidate: true }
                      );
                      dispatch(fetchCity({ country_id: country?.value, index: index }));
                      dispatch(
                        getPriceVrientVendor({
                          color_id: item.attribute_value_color_id,
                          product_id: item.product_id,
                          size_id: item.attribute_value_size_id,
                          country_id: country?.value,
                          city_id: watch(`vendors.${index}.city_id`),
                          index: index
                        })
                      );
                    }}
                    options={countries}
                    value={
                      countries?.find(
                        (opt) =>
                          opt.value ===
                          watch(`vendors.${index}.country_id`)
                      ) || null
                    }
                    isDisabled={true}
                  />
                </div>
                {/* City */}
                <div className="mb-4">
                  <Typography variant="small" className="font-medium capitalize">
                    {t('city_label')}
                  </Typography>
                  <Select
                    placeholder={t('select_city_placeholder')}
                    onChange={(city) => {
                      setValue(
                        `vendors.${index}.city_id`,
                        city?.value || null,
                        { shouldValidate: true }
                      );
                      dispatch(
                        getPriceVrientVendor({
                          color_id: item.attribute_value_color_id,
                          product_id: item.product_id,
                          size_id: item.attribute_value_size_id,
                          country_id: watch(`vendors.${index}.country_id`),
                          city_id: city?.value,
                          index: index
                        })
                      );
                    }}
                    options={cities?.length > 0 ? cities[index] : []}
                    value={
                      cities?.length > 0 ? cities[index]?.find(
                        (opt) =>
                          opt.value ===
                          watch(`vendors.${index}.city_id`)
                      ) || null : null
                    }
                    isDisabled={true}
                  />
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('select_vendor_placeholder')}
                </label>
                <Controller
                  name={`vendors.${index}.vendor_id`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      placeholder={t('select_vendor_placeholder')}
                      options={vendors?.length > 0 ? vendors[index] : []}
                      value={vendors?.length > 0 ? vendors[index]?.find((v) => v.value === field.value) || null : null}
                      isDisabled
                      className="mb-1"
                      // isClearable
                      noOptionsMessage={() => t('no_vendor_available')}
                    />
                  )}
                />

                <div className="mt-4">
                  <Typography variant="small" className="font-medium">
                    {t('pickup_landmark_label')}
                  </Typography>
                  <p className="text-sm text-gray-900 mt-1">
                    {row?.pickup_landmark || t('na')}
                  </p>
                  {row?.latitude != null && row?.longitude != null && (
                    <p className="text-xs text-gray-500 mt-1">
                      {ct('lat')} {Number(row.latitude).toFixed(6)}, {ct('lng')}{" "}
                      {Number(row.longitude).toFixed(6)}
                    </p>
                  )}
                </div>
                <div className="mt-4">
                  <Typography variant="small" className="font-medium">
                    {t('pickup_location_label')}
                  </Typography>
                  <p className="text-sm text-gray-900 mt-1">
                    {row?.pickup_location || t('na')}
                  </p>
                </div>

                <label className="block text-sm font-medium text-gray-700 mt-3">
                  {t('price_label')}
                </label>
                {row?.previous_price && Number(row.previous_price) > 0 && (
                  <div className="text-xs text-gray-500 font-medium flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                    <span>{t('previous_price_label')}:</span>
                    <span className="text-red-600 font-semibold">
                      {item?.product?.rfq_currency_code || ""} {Number(row.previous_price).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex items-stretch">
                  <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-blue-gray-200 rounded-l-md text-sm font-medium text-gray-700">
                    {item?.product?.rfq_currency_code || ""}
                  </div>
                  <Controller
                    name={`vendors.${index}.price`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        min="0"
                        type="number"
                        value={field.value || ""}
                        readOnly
                        className="!border-t-blue-gray-200 bg-gray-100 cursor-not-allowed"
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                        placeholder={t('vendor_price_placeholder')}
                      />
                    )}
                  />
                </div>
                <label className="block text-sm font-medium text-gray-700 mt-3">
                  {t('quantity_label')}
                </label>
                <Controller
                  name={`vendors.${index}.quantity`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="number"
                      min="1"
                      max={item?.quantity}
                      {...field}
                      readOnly
                      className="!border-t-blue-gray-200 bg-gray-100 cursor-not-allowed"
                      labelProps={{
                        className: "before:content-none after:content-none",
                      }}
                      placeholder={t('quantity_label')}
                    />
                  )}
                />

                {fileToPreview && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2">
                      {t('vendor_file_label')}
                    </label>
                    <FilePreview file={fileToPreview} isView />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogBody>

      <DialogFooter className="flex justify-end gap-4 border-t pt-4">
        <button
          type="button"
          onClick={close}
          className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
        >
          {t('close_button')}
        </button>
      </DialogFooter>
    </Dialog>
  );
}

export default ViewVendorVarientPriceModal;
